import React from 'react'
import styled from 'styled-components'

import Tooltip from 'components/Tooltip'
import { FlatButton } from 'components/Button'
import { HoverTrigger } from 'components/HoverTrigger'
import { VersionIconStyled } from 'components/VersionItem/VersionIcon'
import {
  useVersionStar,
  UseVersionStarProps,
} from 'modules/versioning/hooks/useVersionStar'
import {
  useVersionUnstar,
  UseVersionUnstarProps,
} from 'modules/versioning/hooks/useVersionUnstar'

import { ReactComponent as StarIcon } from 'icons/star-17.svg'
import { ReactComponent as SaveIcon } from 'icons/save-17.svg'
import { ReactComponent as SaveIconFirst } from 'icons/save-first-17.svg'

export interface StarVersionProps
  extends UseVersionStarProps,
    UseVersionUnstarProps {
  isStarred?: boolean
  isActive?: boolean
  disableAction?: boolean
  isFirst?: boolean
}

const ActionMessage = {
  Star: 'Star Update',
  Unstar: 'Unstar Update',
} as const

export const StarVersion = (props: StarVersionProps) => {
  const {
    isStarred = false,
    isActive = false,
    disableAction = false,
    isFirst,
  } = props
  const [starVersion] = useVersionStar(props)
  const [unstarVersion] = useVersionUnstar({
    versionIdentifier: props.versionIdentifier,
    isLibrary: props.isLibrary,
  })
  const message = ActionMessage[isStarred ? 'Unstar' : 'Star']

  const action = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()

    if (isStarred) {
      unstarVersion()
      return
    }

    starVersion()
  }

  const renderNonHoverIcon = () => {
    const icon = isStarred ? StarIcon : isFirst ? SaveIconFirst : SaveIcon
    const iconAlt = ActionMessage[isStarred ? 'Unstar' : 'Star']
    const iconTestId = isStarred ? 'update-icon-starred' : 'update-icon-save'

    return (
      <VersionIconStyled
        as={icon}
        status={isActive ? 'active' : 'inactive'}
        alt={iconAlt}
        data-testid={iconTestId}
      />
    )
  }

  const renderHoverIcon = () => {
    const icon = !isStarred ? StarIcon : isFirst ? SaveIconFirst : SaveIcon
    const iconAlt = ActionMessage[isStarred ? 'Unstar' : 'Star']
    const iconTestId = isStarred ? 'update-icon-save' : 'update-icon-starred'

    return (
      <VersionIconStyled
        as={icon}
        status={isActive ? 'active' : 'hover'}
        alt={iconAlt}
        data-testid={iconTestId}
      />
    )
  }

  return (
    <FlexTooltip
      content={message}
      disabled={disableAction}
      spacing="12px"
      placement="bottom">
      <ActionFlatButton
        disabled={disableAction}
        onClick={action}
        data-focus-ring-offset-inset>
        <HoverTrigger
          component={renderNonHoverIcon()}
          hoverComponent={renderHoverIcon()}
        />
      </ActionFlatButton>
    </FlexTooltip>
  )
}

const ActionFlatButton = styled(FlatButton)`
  padding: 0;

  &[disabled] {
    opacity: 1;
  }
`
const FlexTooltip = styled(Tooltip)`
  display: flex;
`
